<template>
    <kt-modal 
        :visible="visible" 
        title="Manage Request Methods"
        size="md"
        @dismiss="$emit('close')"
    >
        <template #body>
            <div class="request-method-selector">
                <!-- Add New Request Method Form (Admin only) -->
                <div v-if="props.canManage" class="card mb-3">
                    <div class="card-header py-2">
                        <strong>{{ editingMethod ? 'Edit Request Method' : 'Add New Request Method' }}</strong>
                    </div>
                    <div class="card-body py-2">
                        <div class="row g-2">
                            <div class="col-md-9">
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    v-model="newMethod.name"
                                    placeholder="Request Method Name (e.g., Phone, Email, Walk-in)"
                                />
                            </div>
                            <div class="col-md-3">
                                <button 
                                    type="button"
                                    class="btn btn-sm btn-primary w-100"
                                    @click="saveMethod"
                                    :disabled="!newMethod.name"
                                >
                                    <Save :size="14" class="me-1" />
                                    {{ editingMethod ? 'Update' : 'Add' }}
                                </button>
                            </div>
                        </div>
                        <div v-if="editingMethod" class="mt-2">
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelEdit"
                            >
                                Cancel Edit
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Request Method List -->
                <div class="card">
                    <div class="card-header py-2">
                        <strong>Select a Request Method</strong>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loading" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm"></span>
                            Loading request methods...
                        </div>
                        <div v-else-if="methods.length === 0" class="text-center py-3 text-muted">
                            No request methods configured. Add one above.
                        </div>
                        <table v-else class="table table-sm table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Method Name</th>
                                    <th v-if="props.canManage" style="width: 100px">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    v-for="method in methods" 
                                    :key="method.name"
                                    :class="{ 'table-primary': selectedMethod === method.name }"
                                    style="cursor: pointer;"
                                    @click="selectMethod(method)"
                                >
                                    <td>{{ method.name }}</td>
                                    <td v-if="props.canManage">
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-primary me-1"
                                            @click.stop="editMethod(method)"
                                            title="Edit"
                                        >
                                            <Edit :size="14" />
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click.stop="deleteMethod(method)"
                                            title="Delete"
                                        >
                                            <Trash2 :size="14" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">
                Cancel
            </button>
            <button 
                type="button" 
                class="btn btn-primary"
                @click="confirmSelection"
                :disabled="!selectedMethod"
            >
                Select Method
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { Save, Edit, Trash2 } from 'lucide-vue-next';

interface RequestMethod {
    name: string;
}

const props = defineProps<{
    visible: boolean;
    currentValue?: string | null;
    canManage?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', method: RequestMethod): void;
    (e: 'methods-changed', methods: RequestMethod[]): void;
}>();

const loading = ref(false);
const methods = ref<RequestMethod[]>([]);
const selectedMethod = ref<string | null>(null);
const editingMethod = ref<RequestMethod | null>(null);
const newMethod = ref<RequestMethod>({ name: '' });

const OPTION_KEY = 'request_methods_json';

const loadMethods = async () => {
    loading.value = true;
    try {
        const optionValue = await OptionsService.get(OPTION_KEY);
        if (optionValue) {
            methods.value = JSON.parse(optionValue);
        } else {
            methods.value = [];
        }
    } catch (error) {
        console.error('Error loading request methods:', error);
        methods.value = [];
    } finally {
        loading.value = false;
    }
};

const saveMethodsToOptions = async () => {
    try {
        await OptionsService.set(OPTION_KEY, JSON.stringify(methods.value));
        emit('methods-changed', methods.value);
    } catch (error) {
        console.error('Error saving request methods:', error);
        Toast.error('Failed to save request methods');
    }
};

const saveMethod = async () => {
    if (!newMethod.value.name.trim()) {
        Toast.error('Method name is required');
        return;
    }

    if (editingMethod.value) {
        // Update existing
        const index = methods.value.findIndex(m => m.name === editingMethod.value!.name);
        if (index !== -1) {
            methods.value[index] = { ...newMethod.value };
        }
        editingMethod.value = null;
    } else {
        // Check for duplicate
        if (methods.value.some(m => m.name.toLowerCase() === newMethod.value.name.toLowerCase())) {
            Toast.error('A request method with this name already exists');
            return;
        }
        // Add new
        methods.value.push({ ...newMethod.value });
    }

    await saveMethodsToOptions();
    newMethod.value = { name: '' };
    Toast.success('Request method saved');
};

const editMethod = (method: RequestMethod) => {
    editingMethod.value = method;
    newMethod.value = { ...method };
};

const cancelEdit = () => {
    editingMethod.value = null;
    newMethod.value = { name: '' };
};

const deleteMethod = async (method: RequestMethod) => {
    Alert.confirm(
        `Are you sure you want to delete "${method.name}"?`,
        async () => {
            methods.value = methods.value.filter(m => m.name !== method.name);
            await saveMethodsToOptions();
            Toast.success('Request method deleted');
            
            if (selectedMethod.value === method.name) {
                selectedMethod.value = null;
            }
        },
        () => {} // Cancel callback
    );
};

const selectMethod = (method: RequestMethod) => {
    selectedMethod.value = method.name;
};

const confirmSelection = () => {
    const method = methods.value.find(m => m.name === selectedMethod.value);
    if (method) {
        emit('select', method);
    }
    emit('close');
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        loadMethods();
        selectedMethod.value = props.currentValue || null;
    }
});

onMounted(() => {
    if (props.visible) {
        loadMethods();
        selectedMethod.value = props.currentValue || null;
    }
});
</script>

<style scoped>
.request-method-selector {
    min-height: 200px;
}
</style>
